import { useState, useEffect, useMemo } from 'react';
import { Puzzle, X, Inbox, Menu } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { syncSessionWithExtension } from './lib/supabase';
import { AuthForm } from './components/AuthForm';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { DndProvider } from './components/DndProvider';
import { DraggableBookmarkCard } from './components/DraggableBookmarkCard';
import { BookmarkCardSkeleton } from './components/BookmarkCardSkeleton';
import { UserMenu } from './components/UserMenu';
import { TourProvider } from './components/TourProvider';
import { Button } from './components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { supabase } from './lib/supabase';
import { collectionsApi, bookmarksApi } from './lib/api';
import { api } from './lib/axios';
import { useMoveBookmarkToCollection, useReorderCollections } from './hooks/use-dnd-operations';
import { useIsMobile } from './hooks/use-media-query';
import { arrayMove } from '@dnd-kit/sortable';
import { getIconByName } from './lib/icons';
import type { Session } from '@supabase/supabase-js';
import type { BookmarkWithTags, CollectionWithCount } from './types/database';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarks, setBookmarks] = useState<BookmarkWithTags[]>([]);
  const [collections, setCollections] = useState<CollectionWithCount[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExtensionConnect, setShowExtensionConnect] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBookmarkIds, setSelectedBookmarkIds] = useState<Set<string>>(new Set());

  // Mobile state
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Reset selection when exiting selection mode
  useEffect(() => {
    if (!selectionMode) {
      setSelectedBookmarkIds(new Set());
    }
  }, [selectionMode]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // DnD mutations
  const moveBookmarkMutation = useMoveBookmarkToCollection();
  const reorderMutation = useReorderCollections();

  // Initialize demo account on app load
  useEffect(() => {
    const initializeDemo = async () => {
      try {
        await api.post('/init-demo');
        console.log('Demo account initialized');
      } catch (error) {
        console.error('Error initializing demo account:', error);
      }
    };

    initializeDemo();
  }, []);

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);

      // Check if coming from extension with existing session
      const params = new URLSearchParams(window.location.search);
      if (params.get('auth_callback') === 'extension' && session) {
        setShowExtensionConnect(true);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      loadCollections();
      // Show skeleton when collection changes (different data set)
      loadBookmarks(true);
    }
  }, [session, selectedCollectionId]);

  const loadCollections = async () => {
    try {
      const data = await collectionsApi.getAll();
      setCollections(data);
    } catch (error) {
      console.error('Error loading collections:', error);
    }
  };

  const loadBookmarks = async (showSkeleton = false) => {
    // Only show skeleton on initial load, not on refetch
    if (showSkeleton || bookmarks.length === 0) {
      setBookmarksLoading(true);
    }
    try {
      const data = await bookmarksApi.getAll({
        collection_id: selectedCollectionId || undefined,
        search: searchQuery || undefined,
        archived: false,
      });

      // Transform to match expected format
      const bookmarksWithTags: BookmarkWithTags[] = data.map((bookmark: any) => ({
        ...bookmark,
        tags: (bookmark.tags || []).map((tag: string) => ({ name: tag })),
      }));

      setBookmarks(bookmarksWithTags);
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    } finally {
      setBookmarksLoading(false);
    }
  };

  // Group bookmarks by collection when viewing all bookmarks
  const groupedBookmarks = useMemo(() => {
    if (selectedCollectionId !== null) {
      return null; // Don't group when a specific collection is selected
    }

    const groups: Map<string | null, BookmarkWithTags[]> = new Map();

    // Initialize groups for all collections (to maintain order)
    collections.forEach((collection) => {
      groups.set(collection.id, []);
    });
    groups.set(null, []); // For uncategorized bookmarks

    // Group bookmarks
    bookmarks.forEach((bookmark) => {
      const collectionId = bookmark.collection_id;
      if (!groups.has(collectionId)) {
        groups.set(collectionId, []);
      }
      groups.get(collectionId)!.push(bookmark);
    });

    // Convert to array and filter out empty groups
    const result: { collection: CollectionWithCount | null; bookmarks: BookmarkWithTags[] }[] = [];

    collections.forEach((collection) => {
      const collectionBookmarks = groups.get(collection.id) || [];
      if (collectionBookmarks.length > 0) {
        result.push({ collection, bookmarks: collectionBookmarks });
      }
    });

    // Add uncategorized bookmarks at the end
    const uncategorized = groups.get(null) || [];
    if (uncategorized.length > 0) {
      result.push({ collection: null, bookmarks: uncategorized });
    }

    return result;
  }, [bookmarks, collections, selectedCollectionId]);

  const handleAuthSuccess = () => {
    // Session will be updated via auth state change listener
  };

  const handleLogout = () => {
    setSession(null);
    setBookmarks([]);
    setCollections([]);
    setSelectedCollectionId(null);
  };

  const handleBookmarkChange = () => {
    loadBookmarks();
    loadCollections();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Selection handlers
  const handleToggleSelection = (bookmarkId: string) => {
    setSelectedBookmarkIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(bookmarkId)) {
        newSet.delete(bookmarkId);
      } else {
        newSet.add(bookmarkId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedBookmarkIds.size === bookmarks.length) {
      setSelectedBookmarkIds(new Set());
    } else {
      setSelectedBookmarkIds(new Set(bookmarks.map((b) => b.id)));
    }
  };

  // Bulk actions
  const handleBulkMove = async (targetCollectionId: string | null) => {
    if (selectedBookmarkIds.size === 0) return;

    try {
      const promises = Array.from(selectedBookmarkIds).map((id) =>
        bookmarksApi.update(id, { collection_id: targetCollectionId })
      );
      await Promise.all(promises);

      const collectionName =
        targetCollectionId === null
          ? '미분류'
          : collections.find((c) => c.id === targetCollectionId)?.name || '컬렉션';
      toast.success(`${selectedBookmarkIds.size}개를 "${collectionName}"으로 이동했어요`);

      setSelectionMode(false);
      handleBookmarkChange();
    } catch (error) {
      console.error('Bulk move error:', error);
      toast.error('이동에 문제가 생겼어요');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBookmarkIds.size === 0) return;

    const confirmDelete = window.confirm(`${selectedBookmarkIds.size}개의 북마크를 삭제할까요?`);
    if (!confirmDelete) return;

    try {
      const promises = Array.from(selectedBookmarkIds).map((id) => bookmarksApi.delete(id));
      await Promise.all(promises);

      toast.success(`${selectedBookmarkIds.size}개를 삭제했어요`);
      setSelectionMode(false);
      handleBookmarkChange();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('삭제에 문제가 생겼어요');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedBookmarkIds.size === 0) return;

    try {
      const promises = Array.from(selectedBookmarkIds).map((id) =>
        bookmarksApi.update(id, { is_archived: true })
      );
      await Promise.all(promises);

      toast.success(`${selectedBookmarkIds.size}개를 보관함으로 이동했어요`);
      setSelectionMode(false);
      handleBookmarkChange();
    } catch (error) {
      console.error('Bulk archive error:', error);
      toast.error('보관에 문제가 생겼어요');
    }
  };

  // DnD handlers
  const handleBookmarkMove = (bookmarkId: string, collectionId: string | null) => {
    moveBookmarkMutation.mutate(
      { bookmarkId, collectionId },
      {
        onSuccess: () => {
          const collection = collections.find((c) => c.id === collectionId);
          if (collectionId && collection) {
            toast.success(`"${collection.name}"으로 이동했어요`);
          } else {
            toast.success('컬렉션에서 제거했어요');
          }
          loadBookmarks();
          loadCollections();
        },
        onError: () => {
          toast.error('이동에 문제가 생겼어요');
        },
      }
    );
  };

  const handleCollectionReorder = (activeId: string, overId: string) => {
    // Optimistically update UI immediately
    const oldIndex = collections.findIndex((c) => c.id === activeId);
    const newIndex = collections.findIndex((c) => c.id === overId);
    const reordered = arrayMove(collections, oldIndex, newIndex);
    setCollections(reordered);

    reorderMutation.mutate(
      { collections, activeId, overId },
      {
        onError: () => {
          toast.error('순서 변경에 문제가 생겼어요');
          loadCollections(); // Rollback
        },
      }
    );
  };

  const handleConnectExtension = async () => {
    if (session) {
      const success = await syncSessionWithExtension(session);
      if (success) {
        toast.success('확장 프로그램이 연결되었어요!');
      } else {
        toast.error('연결에 실패했어요. 확장 프로그램이 설치되어 있는지 확인해주세요.');
      }
    }
    setShowExtensionConnect(false);
  };

  const handleDismissExtensionPrompt = () => {
    setShowExtensionConnect(false);
  };

  useEffect(() => {
    if (session) {
      // Show skeleton when search query changes (different data set)
      loadBookmarks(true);
    }
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <img
            src="/images/logo.png"
            alt="Markly"
            className="mx-auto mb-4 h-16 w-16 animate-pulse"
          />
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <AuthForm onSuccess={handleAuthSuccess} />
        <Toaster />
      </>
    );
  }

  return (
    <TourProvider>
      <div className="flex h-screen flex-col bg-background">
        <Toaster />

        {/* Extension Connection Prompt */}
        {showExtensionConnect && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="mx-4 w-full max-w-sm rounded-lg bg-card p-6 shadow-xl">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Puzzle className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">확장 프로그램 연결</h3>
                </div>
                <button
                  onClick={handleDismissExtensionPrompt}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mb-2 text-sm text-muted-foreground">
                현재 로그인된 계정으로 확장 프로그램에 연결하시겠습니까?
              </p>
              <p className="mb-6 text-sm font-medium">{session?.user?.email}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleDismissExtensionPrompt}
                  className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  취소
                </button>
                <button
                  onClick={handleConnectExtension}
                  className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  연결하기
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 shadow-sm">
          <div className="flex items-center gap-2.5">
            {/* Mobile menu button */}
            {isMobile && (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="mr-1">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <Sidebar
                    collections={collections}
                    selectedCollectionId={selectedCollectionId}
                    onSelectCollection={(id) => {
                      setSelectedCollectionId(id);
                      setMobileMenuOpen(false);
                    }}
                    onCollectionsChange={loadCollections}
                    onBookmarksChange={loadBookmarks}
                  />
                </SheetContent>
              </Sheet>
            )}
            <img src="/images/logo-text.png" alt="Markly" className="h-12 w-auto" />
          </div>
          <UserMenu onLogout={handleLogout} />
        </header>

        {/* Main Content */}
        <DndProvider
          onBookmarkMove={handleBookmarkMove}
          onCollectionReorder={handleCollectionReorder}
        >
          <div className="flex flex-1 overflow-hidden">
            {/* Desktop sidebar */}
            {!isMobile && (
              <Sidebar
                collections={collections}
                selectedCollectionId={selectedCollectionId}
                onSelectCollection={setSelectedCollectionId}
                onCollectionsChange={loadCollections}
                onBookmarksChange={loadBookmarks}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              />
            )}

            <div className="flex flex-1 flex-col overflow-hidden">
              <TopBar
                collections={collections}
                onBookmarkAdded={handleBookmarkChange}
                onSearch={handleSearch}
                resultCount={bookmarks.length}
                isSearching={bookmarksLoading}
                selectionMode={selectionMode}
                onToggleSelectionMode={() => setSelectionMode(!selectionMode)}
                selectedCount={selectedBookmarkIds.size}
                totalCount={bookmarks.length}
                onSelectAll={handleSelectAll}
                onBulkMove={handleBulkMove}
                onBulkDelete={handleBulkDelete}
                onBulkArchive={handleBulkArchive}
              />

              <main className="flex-1 overflow-y-auto bg-background p-4" data-tour="bookmark-list">
                {bookmarksLoading ? (
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <BookmarkCardSkeleton key={i} />
                    ))}
                  </div>
                ) : bookmarks.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <img
                      src="/images/logo-sleep.png"
                      alt="No bookmarks"
                      className="mb-4 h-32 w-32"
                    />
                    <h2 className="mb-2 text-2xl font-semibold">북마크가 없습니다</h2>
                    <p className="mb-4 text-muted-foreground">
                      {searchQuery ? '검색 결과가 없습니다' : '첫 번째 북마크를 추가해보세요!'}
                    </p>
                  </div>
                ) : groupedBookmarks ? (
                  // Grouped view when no collection is selected
                  <div className="space-y-6">
                    {groupedBookmarks.map((group) => {
                      const IconComponent = group.collection
                        ? getIconByName(group.collection.icon || 'folder')
                        : Inbox;
                      const iconColor = group.collection?.color || '#6b7280';

                      return (
                        <section key={group.collection?.id || 'uncategorized'}>
                          <div className="mb-3 flex items-center gap-2">
                            <IconComponent className="h-5 w-5" style={{ color: iconColor }} />
                            <h2 className="text-lg font-semibold text-foreground">
                              {group.collection?.name || '미분류'}
                            </h2>
                            <span className="text-sm text-muted-foreground">
                              ({group.bookmarks.length})
                            </span>
                          </div>
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                            {group.bookmarks.map((bookmark) => (
                              <DraggableBookmarkCard
                                key={bookmark.id}
                                bookmark={bookmark}
                                collections={collections}
                                onUpdate={handleBookmarkChange}
                                selectionMode={selectionMode}
                                isSelected={selectedBookmarkIds.has(bookmark.id)}
                                onToggleSelection={() => handleToggleSelection(bookmark.id)}
                              />
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  // Flat view when a specific collection is selected
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {bookmarks.map((bookmark) => (
                      <DraggableBookmarkCard
                        key={bookmark.id}
                        bookmark={bookmark}
                        collections={collections}
                        onUpdate={handleBookmarkChange}
                        selectionMode={selectionMode}
                        isSelected={selectedBookmarkIds.has(bookmark.id)}
                        onToggleSelection={() => handleToggleSelection(bookmark.id)}
                      />
                    ))}
                  </div>
                )}
              </main>
            </div>
          </div>
        </DndProvider>
      </div>
    </TourProvider>
  );
}
